// src/components/home/ShareModal.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import {
  doc, updateDoc, increment, addDoc, collection, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed, trying fallback:', err);
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (err) {
    console.warn('Clipboard fallback failed:', err);
    return false;
  }
};

const isMobile = () =>
  typeof navigator !== 'undefined' &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

/* ================================================================== */
/* Toast                                                               */
/* ================================================================== */

const Toast = ({ message, type = 'success' }) => (
  <div
    role="status"
    aria-live="polite"
    className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-[200] px-4 py-2 rounded-full text-white text-sm font-medium shadow-lg flex items-center gap-2 ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    }`}
    style={{ bottom: 'calc(6rem + env(safe-area-inset-bottom))' }}
  >
    <i
      className={`fas ${
        type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
      }`}
      aria-hidden="true"
    />
    {message}
  </div>
);

/* ================================================================== */
/* Post Preview                                                        */
/* ================================================================== */

const PostPreview = ({ post }) => {
  const coverMedia =
    Array.isArray(post.media) && post.media.length
      ? post.media.find((m) => m.isCover) || post.media[0]
      : Array.isArray(post.photos) && post.photos.length
      ? { url: post.photos[0], type: 'image' }
      : null;

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 flex items-center gap-3 mb-4">
      {coverMedia ? (
        <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-600 flex-shrink-0">
          {coverMedia.type === 'video' ? (
            <video
              src={coverMedia.url}
              className="w-full h-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={coverMedia.url}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
      ) : (
        <div className="w-14 h-14 rounded-lg bg-orange/10 flex items-center justify-center flex-shrink-0">
          <i className="fas fa-file-alt text-orange" aria-hidden="true" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-800 dark:text-white truncate">
          {post.authorName || 'Post'}
        </p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2 leading-snug">
          {post.content?.slice(0, 80) || 'Check out this post'}
        </p>
      </div>
    </div>
  );
};

/* ================================================================== */
/* ShareModal                                                          */
/* ================================================================== */

const ShareModal = ({ post, onClose }) => {
  const { user, userData } = useAuth();

  const [toast, setToast] = useState(null);
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const toastTimerRef = useRef(null);

  /* -------------------- Effects -------------------- */

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busyRef.current) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const showToast = useCallback((message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setToast(null);
    }, 2400);
  }, []);

  /* -------------------- Share helpers -------------------- */

  const postUrl = `${window.location.origin}/post/${post.id}`;

  const bumpShareCount = async (method = 'copy_link') => {
    if (shared || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);

    let postOk = false;
    let logOk = false;

    try {
      await updateDoc(doc(db, 'posts', post.id), {
        shareCount: increment(1),
      });
      postOk = true;
    } catch (err) {
      console.warn('share counter failed:', err);
    }

    try {
      await addDoc(collection(db, 'shares'), {
        postId: post.id,
        sharedBy: user?.uid || null,
        sharedByName: userData?.displayName || user?.email || 'User',
        method,
        createdAt: serverTimestamp(),
      });
      logOk = true;
    } catch (err) {
      console.warn('share log failed:', err);
    }

    if (mountedRef.current) {
      if (postOk || logOk) setShared(true);
      setBusy(false);
    }
    busyRef.current = false;
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(postUrl);
    if (!ok) {
      showToast('Could not copy link', 'error');
      return;
    }
    if (mountedRef.current) {
      setCopied(true);
      showToast('Link copied!');
      setTimeout(() => {
        if (mountedRef.current) setCopied(false);
      }, 2000);
    }
    bumpShareCount('copy_link');
  };

  const shareTo = (platform) => {
    const rawText = post.content?.trim()
      ? post.content.slice(0, 100)
      : `Check out this post by ${post.authorName || 'a member'}`;
    const text = encodeURIComponent(rawText);
    const url = encodeURIComponent(postUrl);

    const mobile = isMobile();

    // Deep links for mobile (fall back to web)
    const deepLinks = {
      whatsapp: mobile ? `whatsapp://send?text=${text}%20${url}` : null,
      facebook: mobile
        ? `fb://share?u=${url}`
        : null,
      twitter: null,
      linkedin: null,
    };

    const webUrls = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };

    const open = (target) => {
      window.open(target, '_blank', 'noopener,noreferrer,width=600,height=500');
    };

    // Try deep link first on mobile; fall back to web URL
    const deepLink = deepLinks[platform];
    if (mobile && deepLink) {
      const start = Date.now();
      const newWin = window.open(deepLink, '_blank');
      // If the app isn't installed, the window closes quickly.
      setTimeout(() => {
        if (newWin && newWin.closed) open(webUrls[platform]);
      }, 400);
      // Fallback if nothing happened after 1.5s
      setTimeout(() => {
        if (Date.now() - start < 1400) open(webUrls[platform]);
      }, 1500);
    } else {
      open(webUrls[platform]);
    }

    bumpShareCount(platform);
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: 'IIEE-ERCSA Post',
        text: post.content?.slice(0, 100) || 'Check out this post',
        url: postUrl,
      });
      showToast('Shared!');
      bumpShareCount('native');
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.warn('share failed', err);
      }
    }
  };

  /* -------------------- Render -------------------- */

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const mobile = isMobile();

  return (
    <>
      <div
        className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
        onClick={() => !busy && onClose?.()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div
          className="bg-white dark:bg-gray-800 w-full sm:rounded-2xl max-w-md max-h-full sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl rounded-t-2xl sm:rounded-b-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle (mobile) */}
          <div className="sm:hidden flex justify-center pt-2 pb-1 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>

          {/* Header */}
          <div className="px-5 py-3 border-b dark:border-gray-700 flex justify-between items-center flex-shrink-0">
            <h3
              id="share-modal-title"
              className="font-bold text-gray-800 dark:text-white"
            >
              Share Post
            </h3>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label="Close"
              className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 flex items-center justify-center transition"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 sm:p-5 overflow-y-auto flex-1">
            {/* Post preview */}
            <PostPreview post={post} />

            {/* Native share (mobile only) */}
            {canNativeShare && (
              <button
                type="button"
                onClick={nativeShare}
                disabled={busy}
                className="w-full bg-orange hover:bg-orange-dark text-white py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 mb-4 transition"
              >
                <i className="fas fa-share-nodes" aria-hidden="true" />
                Share via…
              </button>
            )}

            {/* Social buttons — hide WhatsApp/FB on desktop unless native share available */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-4">
              {(!mobile || canNativeShare) && (
                <button
                  type="button"
                  onClick={() => shareTo('whatsapp')}
                  disabled={busy}
                  className="flex flex-col items-center gap-1.5 text-[11px] dark:text-gray-200 disabled:opacity-50 transition active:scale-95"
                >
                  <div className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-sm">
                    <i className="fab fa-whatsapp text-xl" aria-hidden="true" />
                  </div>
                  <span className="truncate w-full text-center">WhatsApp</span>
                </button>
              )}

              {(!mobile || canNativeShare) && (
                <button
                  type="button"
                  onClick={() => shareTo('facebook')}
                  disabled={busy}
                  className="flex flex-col items-center gap-1.5 text-[11px] dark:text-gray-200 disabled:opacity-50 transition active:scale-95"
                >
                  <div className="w-12 h-12 rounded-full bg-[#1877F2] text-white flex items-center justify-center shadow-sm">
                    <i className="fab fa-facebook-f text-xl" aria-hidden="true" />
                  </div>
                  <span className="truncate w-full text-center">Facebook</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => shareTo('twitter')}
                disabled={busy}
                className="flex flex-col items-center gap-1.5 text-[11px] dark:text-gray-200 disabled:opacity-50 transition active:scale-95"
              >
                <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center shadow-sm">
                  <i className="fab fa-x-twitter text-xl" aria-hidden="true" />
                </div>
                <span className="truncate w-full text-center">X</span>
              </button>

              <button
                type="button"
                onClick={() => shareTo('linkedin')}
                disabled={busy}
                className="flex flex-col items-center gap-1.5 text-[11px] dark:text-gray-200 disabled:opacity-50 transition active:scale-95"
              >
                <div className="w-12 h-12 rounded-full bg-[#0A66C2] text-white flex items-center justify-center shadow-sm">
                  <i className="fab fa-linkedin-in text-xl" aria-hidden="true" />
                </div>
                <span className="truncate w-full text-center">LinkedIn</span>
              </button>
            </div>

            {/* Copy link */}
            <div className="border-t dark:border-gray-700 pt-4">
              <label
                htmlFor="share-url"
                className="block text-xs text-gray-500 dark:text-gray-400 mb-2"
              >
                Or copy link
              </label>
              <div className="flex gap-2">
                <input
                  id="share-url"
                  readOnly
                  value={postUrl}
                  onFocus={(e) => e.target.select()}
                  onClick={(e) => e.target.select()}
                  className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2.5 text-xs dark:text-white truncate min-w-0"
                  aria-label="Shareable post URL"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  disabled={busy && !copied}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium text-white transition whitespace-nowrap ${
                    copied
                      ? 'bg-green-500'
                      : 'bg-gray-700 hover:bg-gray-800 disabled:opacity-60'
                  }`}
                >
                  {copied ? (
                    <>
                      <i className="fas fa-check mr-1" aria-hidden="true" />
                      Copied
                    </>
                  ) : (
                    <>
                      <i className="fas fa-copy mr-1" aria-hidden="true" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p className="sr-only" role="status" aria-live="polite">
                {copied ? 'Link copied to clipboard' : ''}
              </p>
            </div>
          </div>

          {/* Footer safe-area spacer for mobile */}
          <div className="sm:hidden flex-shrink-0" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
        </div>
      </div>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} />}
    </>
  );
};

export default ShareModal;